import { useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOLabel from "@atlaskit/react-ufo/label";
import { token } from "@atlaskit/tokens";

import { LinkWithIcon } from "@/components/global-nav/list-of-links/src/link-with-icon";
import { Link } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
    width: "100%",
    maxWidth: "1216px",
    margin: "auto",
    paddingBlock: "24px",
    paddingInline: token("space.200"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.400"),

    [media.above.sm]: {
        paddingInline: "44px",
        gap: token("space.400")
    },

    [media.above.md]: {
        paddingInline: "24px",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center"
    }
});

const linkStyle = css({
    "fontSize": "14px",
    "lineHeight": "14px",
    "height": "14px",
    "textDecoration": "none",
    "color": wacToken("color.primary.black"),

    ":hover": {
        textDecoration: "underline",
        textShadow: "none"
    },

    ":visited, :focus": {
        textShadow: "none"
    }
});

const copyrightStyle = css({
    color: wacToken("color.neutral.light800"),
    lineHeight: "18px",
    height: "18px",
    fontSize: "14px",

    [media.above.md]: {
        textAlign: "center"
    }
});

const linksAndLanguageStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),

    [media.above.sm]: {
        flexDirection: "row",
        gap: token("space.400"),
        alignItems: "center"
    }
});

const listStyle = css({
    listStyleType: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),

    [media.above.sm]: {
        flexDirection: "row",
        gap: token("space.400"),
        alignItems: "center"
    }
});

const bottomViewAllLinkLiAnchorStyle = css({
    "color": wacToken("color.primary.black"),
    "fontSize": "0.875rem",
    "textDecoration": "none",
    "letterSpacing": "0.42px",
    "fontFamily": "Charlie Display",
    "&:hover,&:visited,&:focus": {
        textDecoration: "underline"
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")} !important`
    }
});

// ==================================================================
// Type Props
// ==================================================================
export interface GlobalFooterBottomProps {
    className?: string;
}

// ==================================================================
// Rendering Functions
// ==================================================================
const BottomLinks = () => {
    const intl = useIntl();

    useEffect(() => {
        const w = window as Window &
            typeof globalThis & {
                AtlOneTrustWrapper?: { updatePolicyLinks?: () => void };
            };

        if (w.AtlOneTrustWrapper?.updatePolicyLinks) {
            w.AtlOneTrustWrapper.updatePolicyLinks();
        }
    }, []);

    const bottomLinks = [
        {
            text: intl.formatMessage({
                id: "bitbucket.globalfooter.bottom.privacy-policy",
                defaultMessage: "Privacy policy"
            }),
            href: useLocalizedHref("/legal/privacy-policy"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "bitbucket.globalfooter.bottom.notice-at-collection",
                defaultMessage: "Notice at Collection"
            }),
            href: useLocalizedHref(
                "/legal/privacy-policy#additional-disclosures-for-ca-residents"
            ),
            isOneTrustWrapped: true
        },
        {
            text: intl.formatMessage({
                id: "bitbucket.globalfooter.bottom.terms",
                defaultMessage: "Terms of use"
            }),
            href: useLocalizedHref("/legal/cloud-terms-of-service"),
            isOneTrustWrapped: false
        },
        {
            text: intl.formatMessage({
                id: "bitbucket.globalfooter.bottom.trust.security",
                defaultMessage: "Trust & security"
            }),
            href: useLocalizedHref("/legal/atlassian-customer-agreement#intro"),
            isOneTrustWrapped: false
        }
    ];

    const bottomViewAllLink = {
        text: intl.formatMessage({
            id: "bitbucket.globalfooter.bottom.view-all-products",
            defaultMessage: "View all Atlassian products"
        }),
        href: useLocalizedHref("/software"),
        bgColor: "bgWhite"
    };

    return (
        <>
            {bottomLinks.map((bottomLink) => {
                if (bottomLink.isOneTrustWrapped) {
                    return (
                        <li
                            key={bottomLink.text}
                            className={"link-wrapper atl-policy-link-container"}
                        >
                            <LinkWithIcon
                                linkText={bottomLink.text}
                                href={bottomLink.href}
                                type="anchor"
                                isHaveIcon={false}
                                isUsePlainSpanTagForLinkText={true}
                                className={"atl-policy-link"}
                                spanLinkTextClassName={"atl-policy-link-text"}
                                css={linkStyle}
                            />
                        </li>
                    );
                } else {
                    return (
                        <li key={bottomLink.text}>
                            <Link
                                text={bottomLink.text}
                                href={bottomLink.href}
                                type="anchor"
                                bgColor="bgColored"
                                css={linkStyle}
                                aria-label="footer-link"
                            />
                        </li>
                    );
                }
            })}
            <li key={bottomViewAllLink.text.trim().replace(" ", "-")}>
                <Link
                    css={bottomViewAllLinkLiAnchorStyle}
                    text={bottomViewAllLink.text}
                    href={bottomViewAllLink.href}
                    type="primary"
                    bgColor={"bgColored"}
                    aria-label="footer-link"
                />
            </li>
        </>
    );
};

const copyRightYear = new Date().getFullYear();

export const BitbucketFooterBottom = ({
    className
}: GlobalFooterBottomProps) => {
    return (
        <UFOLabel name="global-footer-v2-bottom">
            <div className={className} css={containerStyle}>
                <div css={copyrightStyle} data-testid="footer-copyright">
                    Copyright © {copyRightYear} Atlassian
                </div>
                <div css={linksAndLanguageStyle}>
                    <UFOLabel name="global-footer-v2-bottom-links">
                        <ul css={listStyle}>
                            <BottomLinks />
                        </ul>
                    </UFOLabel>
                </div>
            </div>
        </UFOLabel>
    );
};
